/**
 * @fileoverview Custom React Query hook for admin client data
 *
 * Provides data fetching for the admin clients page with automatic
 * caching, background updates, and error handling through React Query.
 *
 * Features:
 * - Fetch all clients with optional filtering
 * - Filter by client status
 * - Filter by project status
 * - Show/hide test accounts (IT users only)
 * - Automatic data refetching and caching
 *
 * @requires react-query
 * @requires firebase
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions();

/**
 * Fetch all clients from Airtable via Firebase Function
 * @param {Object} options - Filter options
 * @param {string} options.status - Client status filter
 * @param {string} options.projectStatus - Project status filter
 * @param {boolean} options.showTestAccounts - Include test accounts (IT only)
 * @param {string} options.hasProject - Filter by project existence ('all', 'hasProject', 'noProject')
 * @returns {Promise<Array>} Array of client objects
 */
const fetchAllClients = async ({
  status = "all",
  projectStatus = "all",
  showTestAccounts = false,
  hasProject = "all",
}) => {
  const functions = getFunctions();
  const getAllClientsForAdmin = httpsCallable(
    functions,
    "getAllClientsForAdmin",
  );

  try {
    const result = await getAllClientsForAdmin({
      status,
      projectStatus,
      showTestAccounts,
      hasProject,
    });

    if (!result.data.success) {
      throw new Error(result.data.message || "Failed to fetch clients");
    }

    return result.data.data || [];
  } catch (error) {
    console.error("Error fetching clients:", error);
    throw error;
  }
};

/**
 * Hook to fetch and manage admin clients data
 * @param {Object} options - Query options
 * @param {string} options.status - Client status filter
 * @param {string} options.projectStatus - Project status filter
 * @param {boolean} options.showTestAccounts - Include test accounts
 * @param {string} options.hasProject - Filter by project existence
 * @returns {Object} React Query result with clients data
 */
export const useAdminClients = (options = {}) => {
  return useQuery({
    queryKey: ["adminClients", options],
    queryFn: () => fetchAllClients(options),
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetch single client details by ID
 * @param {string} clientId - Client record ID
 * @returns {Promise<Object>} Client object with full details
 */
const fetchClientById = async (clientId) => {
  const functions = getFunctions();
  const getClientById = httpsCallable(functions, "getClientById");

  try {
    const result = await getClientById({ clientId });

    if (!result.data.success) {
      throw new Error(result.data.message || "Failed to fetch client");
    }

    return result.data.data;
  } catch (error) {
    console.error("Error fetching client:", error);
    throw error;
  }
};

/**
 * Hook to fetch single client details
 * @param {string} clientId - Client record ID
 * @param {Object} options - React Query options
 * @returns {Object} React Query result with client data
 */
export const useAdminClient = (clientId, options = {}) => {
  return useQuery({
    queryKey: ["adminClient", clientId],
    queryFn: () => fetchClientById(clientId),
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    ...options,
  });
};

/**
 * Hook for creating a new client
 * @return {Object} React Query mutation object
 */
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clientData) => {
      const createClientFn = httpsCallable(functions, "createClient");
      const result = await createClientFn(clientData);
      return result.data;
    },
    onSuccess: () => {
      // Invalidate clients list to refresh
      queryClient.invalidateQueries({ queryKey: ["adminClients"] });
    },
    onError: (error) => {
      console.error("[useCreateClient] Error:", error);
    },
  });
};

/**
 * Hook for updating an existing client
 * @return {Object} React Query mutation object
 */
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, updates }) => {
      const updateClientFn = httpsCallable(functions, "updateClient");
      const result = await updateClientFn({ clientId, updates });
      return result.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate both the list and the individual client
      queryClient.invalidateQueries({ queryKey: ["adminClients"] });
      queryClient.invalidateQueries({
        queryKey: ["adminClient", variables.clientId],
      });
    },
    onError: (error) => {
      console.error("[useUpdateClient] Error:", error);
    },
  });
};
